import React, { useState, useEffect } from "react";
import "./ProductListToolbar.css";

const ProductFilterPopup = ({
  isOpen,
  onClose,
  fields,
  filters,
  setFilters,
  dropdownData,
  onDownloadFiltered,
  showDownloadButton = true,
}) => {
  const [tempFilters, setTempFilters] = useState({});
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    const initial = {};
    fields.forEach((f) => {
      if (f.type === "date") {
        initial[`${f.fields}_from`] = filters[`${f.fields}_from`] || "";
        initial[`${f.fields}_to`] = filters[`${f.fields}_to`] || "";
      } else {
        initial[f.fields] = filters[f.fields] || "";
      }
    });
    setTempFilters(initial);
  }, [filters, isOpen]);

  const triggerClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      onClose();
    }, 400);
  };

  const handleSubmit = () => {
    setFilters(tempFilters);
    triggerClose();
  };

  const handleClear = () => {
    const cleared = {};
    fields.forEach((f) => {
      if (f.type === "date") {
        cleared[`${f.fields}_from`] = "";
        cleared[`${f.fields}_to`] = "";
      } else {
        cleared[f.fields] = "";
      }
    });
    setTempFilters(cleared);
    setFilters(cleared);
    triggerClose();
  };

  if (!isOpen && !isClosing) return null;

  return (
    <div className="filter-overlay" onClick={triggerClose}>
      <div
        className={`filter-container ${isClosing ? "closing" : ""}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="filter-header">
          <h2 className="filter-title">Filter Table</h2>
          <button className="filter-close-btn" onClick={triggerClose}>
            ✖
          </button>
        </div>
        <div className="filter-body">
          {fields.map((field) => (
            <div className="filter-field" key={field.fields}>
              <label className="filter-label">
                {field.fields
                  .replace(/([A-Z])/g, " $1")
                  .replace(/^./, (str) => str.toUpperCase())}
              </label>
              {field.type === "date" ? (
                <div className="filter-date-range">
                  <input
                    className="filter-input"
                    type="date"
                    value={tempFilters[`${field.fields}_from`] || ""}
                    onChange={(e) =>
                      setTempFilters({
                        ...tempFilters,
                        [`${field.fields}_from`]: e.target.value,
                      })
                    }
                    placeholder="From Date"
                  />
                  <span>to</span>
                  <input
                    className="filter-input"
                    type="date"
                    value={tempFilters[`${field.fields}_to`] || ""}
                    onChange={(e) =>
                      setTempFilters({
                        ...tempFilters,
                        [`${field.fields}_to`]: e.target.value,
                      })
                    }
                    placeholder="To Date"
                  />
                </div>
              ) : field.api ? (
                <select
                  className="filter-select"
                  value={tempFilters[field.fields] || ""}
                  onChange={(e) =>
                    setTempFilters({
                      ...tempFilters,
                      [field.fields]: e.target.value,
                    })
                  }
                >
                  <option value="">All</option>
                  {Array.isArray(dropdownData[field.fields]) &&
                    dropdownData[field.fields].map((opt) => {
                      const value = opt?.id ?? opt?._id;
                      const label = String(
                        opt?.label ?? opt?.name ?? "Unnamed"
                      );
                      return (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      );
                    })}
                </select>
              ) : (
                <input
                  className="filter-input"
                  type={field.type === "number" ? "number" : "text"}
                  value={tempFilters[field.fields] || ""}
                  onChange={(e) =>
                    setTempFilters({
                      ...tempFilters,
                      [field.fields]: e.target.value,
                    })
                  }
                  placeholder={`Filter ${field.fields}`}
                />
              )}
            </div>
          ))}
        </div>
        <div className="filter-footer">
          <button
            className="filter-apply-btn"
            onClick={handleSubmit}
            title="Apply Filters"
          >
            <i className="fas fa-check-circle"></i>
          </button>
          <button
            className="filter-clear-btn"
            onClick={handleClear}
            title="Clear Filters"
          >
            <i className="fas fa-broom"></i>
          </button>
          {showDownloadButton && (
            <button
              className="filter-download-btn"
              onClick={onDownloadFiltered}
              title="Download Filtered"
            >
              <i className="fas fa-download"></i>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const PaginationControls = ({
  currentPage,
  productsPerPage,
  filteredLength,
  paginate,
}) => {
  const totalPages = Math.ceil(filteredLength / productsPerPage);
  const maxVisible = 3; // Show 3 pages before ellipsis

  const renderPageNumbers = () => {
    const buttons = [];

    for (let i = 1; i <= totalPages; i++) {
      if (
        i === 1 ||
        i === totalPages ||
        (i >= currentPage - 1 && i <= currentPage + 1)
      ) {
        buttons.push(
          <button
            key={i}
            onClick={() => paginate(i)}
            className={currentPage === i ? "active" : ""}
          >
            {i}
          </button>
        );
      } else if (
        (i === currentPage - 2 && currentPage > 4) ||
        (i === currentPage + 2 && currentPage < totalPages - 3)
      ) {
        buttons.push(
          <span key={`ellipsis-${i}`} className="pagination-ellipsis">
            ...
          </span>
        );
      }
    }

    return buttons;
  };

  return (
    <div className="pagination">
      <button
        onClick={() => paginate(currentPage - 1)}
        disabled={currentPage === 1}
      >
        <i className="fas fa-chevron-left" />
      </button>
      {renderPageNumbers()}
      <button
        onClick={() => paginate(currentPage + 1)}
        disabled={currentPage === totalPages}
      >
        <i className="fas fa-chevron-right" />
      </button>
    </div>
  );
};

const ProductListToolbar = ({
  isFilterOpen,
  setIsFilterOpen,
  productsPerPage,
  setProductsPerPage,
  setCurrentPage,
  handleRefresh,
  totalEntries,
  filters,
  setFilters,
  fields,
  dropdownData,
  onDownloadFiltered,
  currentPage,
  filteredLength,
  paginate,
  searchQuery,
  setSearchQuery  
}) => {
  return (
    <>
      <ProductFilterPopup
        isOpen={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        fields={fields}
        filters={filters}
        setFilters={setFilters}
        dropdownData={dropdownData}
        onDownloadFiltered={onDownloadFiltered}
      />
      <div className="entries-toolbar-container">

      <div className="toolbar-search-wrapper">
          <span className="toolbar-search-icon">
            <i className="fas fa-search"></i>
          </span>
          <input
            type="text"
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="toolbar-search-input"
          />
        </div>
        
        <label htmlFor="entriesPerPage">Entries per page:</label>
        <input
          type="number"
          id="entriesPerPage"
          value={productsPerPage}
          onChange={(e) => {
            const value = parseInt(e.target.value, 10);
            if (!isNaN(value) && value > 0) {
              setProductsPerPage(value);
              setCurrentPage(1);
            }
          }}
          min="1"
          className="entries-input"
        />
        <button
          className="refresh-icon-btn"
          onClick={handleRefresh}
          title="Refresh Table"
        >
          <i className="fas fa-rotate-right"></i>
        </button>
        <button className="filter-btn" onClick={() => setIsFilterOpen(true)}>
          <i className="fas fa-filter"></i> Filter
        </button>
        <PaginationControls
          currentPage={currentPage}
          productsPerPage={productsPerPage}
          filteredLength={filteredLength}
          paginate={paginate}
        />
        <span className="total-entries">
          Total entries: {totalEntries || "Loading..."}
        </span>
      </div>
    </>
  );
};

export default ProductListToolbar;
